import { execOnLoad, formatPrice } from '@general/helpers';
import { debounce, throttle } from 'lodash';
import './filters.scss';
import { initPagination } from '@services/pagination';
import { closeModal } from '@components/modals';

if (!window.dAgency) window.dAgency = {};
window.dAgency.initFilters = (context) => {
  const snippetContainer = context.classList.contains('.filters__container')
    ? context
    : context.closest('.filters__container');
  if (!snippetContainer) return;

  const form = snippetContainer.querySelector('.filters__form');
  if (!form) return;
  const modal = snippetContainer.closest('.modal');
  const filterContentSelector = '.filters__content';
  const itemsCounterSelector = '[data-items-counter]';
  const singleFilterCounterSelector = '[single-filter-active-counter]';
  const resetTrigger = form.querySelector('.filters__remove');
  const resultsWrapper = snippetContainer
    .closest('.shopify-section')
    .querySelector(filterContentSelector);
  const itemCounters = snippetContainer
    .closest('.shopify-section')
    .querySelectorAll(itemsCounterSelector);
  if (!resultsWrapper) {
    console.error(
      `Cannot find ${filterContentSelector} element. Cannot initialize filters`,
    );
    return;
  }
  const urlCache = {};

  const updateCounters = (form) => {
    const newCounters = form.querySelectorAll(singleFilterCounterSelector);
    newCounters.forEach((singleFilterCounter) => {
      const inputsContainer = singleFilterCounter.closest(
        '.filters__accordion',
      );
      const activeInputsCounter = inputsContainer.querySelectorAll(
        'input[type="checkbox"]:checked',
      ).length;
      if (activeInputsCounter > 0) {
        singleFilterCounter.innerHTML = '(' + activeInputsCounter + ')';
        singleFilterCounter.style.display = 'block';
      } else {
        singleFilterCounter.style.display = 'none';
      }
    });
  };

  const resetFilters = () => {
    resetTrigger.addEventListener('click', () => {
      const filterContainers = form.querySelectorAll(
        '.filters__accordion:not([data-index="sorting"])',
      );
      filterContainers.forEach((filter) => {
        const actives = filter.querySelectorAll(
          'input[type="checkbox"]:checked',
        );
        actives.forEach((active) => {
          active.checked = false;
        });

        const priceRange = filter.querySelector('.filters__price');
        if (priceRange) {
          const rangeMin = priceRange.querySelector(
            'input[type="range"][range-min]',
          );
          const rangeMax = priceRange.querySelector(
            'input[type="range"][range-max]',
          );
          if (rangeMin) {
            rangeMin.value = rangeMin.min;
          }
          if (rangeMax) {
            rangeMax.value = rangeMax.max;
          }
          const minValueLabel = priceRange.querySelectorAll(
            '.filters__price-min, .filters__price-value-min',
          );
          const maxValueLabel = priceRange.querySelectorAll(
            '.filters__price-max, .filters__price-value-max',
          );
          const rangeIndicator = priceRange.querySelector('.range-indicator');

          if (rangeIndicator) {
            rangeIndicator.style = '';
          }

          minValueLabel.forEach(
            (label) =>
              (label.innerText = formatPrice({
                format: window?.dAgency?.strings?.moneyFormat?.replace(
                  /{{.*?}}/,
                  '{{amount_no_decimals_with_comma_separator}}',
                ),
                value: parseInt(rangeMin.value) * 100,
              })),
          );

          maxValueLabel.forEach(
            (label) =>
              (label.innerText = formatPrice({
                format: window?.dAgency?.strings?.moneyFormat?.replace(
                  /{{.*?}}/,
                  '{{amount_no_decimals_with_comma_separator}}',
                ),
                value: parseInt(rangeMax.value) * 100,
              })),
          );
        }
      });
    });
  };

  resetFilters();

  const getPageContent = async (url) => {
    if (!url) return;
    if (!urlCache[url]) {
      const page = await fetch(url).then((res) => res.text());
      urlCache[url] = page;
    }
    return new DOMParser().parseFromString(urlCache[url], 'text/html');
  };

  const updatePage = debounce(async () => {
    if (modal) closeModal(modal);
    const searchParamsInit = new URLSearchParams(new FormData(form)).toString();
    // Filters that require availability
    const filterWithAvailability = ['filter.v.option.size'];
    const checkAvailability = filterWithAvailability.some(filter => searchParamsInit.includes(filter));
    // Add availability param
    let searchParams = searchParamsInit;
    if (checkAvailability) {
      searchParams = 'filter.v.availability=1&' + searchParamsInit;
    }
    const newUrl = `${window.location.pathname}?${searchParams}`;
    const pageContent = await getPageContent(newUrl);
    const newForm = pageContent.querySelector(`#${form.id}`);
    const newContent = newForm
      .closest('.shopify-section')
      .querySelector(filterContentSelector);
    resultsWrapper.innerHTML = newContent.innerHTML;
    history.replaceState(null, '', newUrl);
    if (typeof window?.dAgency?.initProductTile === 'function')
      resultsWrapper
        .querySelectorAll('.product-tile__container')
        .forEach((tile) => window.dAgency.initProductTile(tile));
    initPagination({
      callbacks: {
        afterFetch: ({ components: { contentWrapperEl } }) => {
          if (typeof window?.dAgency?.initProductTile === 'function')
            contentWrapperEl
              .querySelectorAll('.product-tile__container')
              .forEach((tile) => window.dAgency.initProductTile(tile));
        },
      },
    });
    const newCount = newForm
      .closest('.shopify-section')
      .querySelector(itemsCounterSelector);
    if (newCount)
      itemCounters.forEach(
        (itemCounter) => (itemCounter.innerHTML = newCount.innerHTML),
      );
    updateCounters(form);
  }, 500);

  const debounceLoading = throttle(
    () => resultsWrapper.classList.toggle('loading'),
    1000,
  );

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (/-Search$/.test(e.target.id)) return;
    debounceLoading();
    await updatePage();
    debounceLoading();
  });

  const sortingWrapper = form.querySelector('.filters__sorting');
  if (sortingWrapper) {
    const select = sortingWrapper.querySelector('select');
    const text = sortingWrapper.querySelector('span');

    if (select && text)
      select.addEventListener(
        'change',
        (e) => (text.innerText = e.target.options[e.target.selectedIndex].text),
      );
  }

  const priceRange = form.querySelector('.filters__price');
  if (priceRange) {
    const rangeMin = priceRange.querySelector('input[type="range"][range-min]');
    const rangeMax = priceRange.querySelector('input[type="range"][range-max]');
    const minValueLabel = priceRange.querySelectorAll(
      '.filters__price-min, .filters__price-value-min',
    );
    const maxValueLabel = priceRange.querySelectorAll(
      '.filters__price-max, .filters__price-value-max',
    );

    rangeMin.addEventListener('input', () =>
      minValueLabel.forEach(
        (label) =>
          (label.innerText = formatPrice({
            format: window?.dAgency?.strings?.moneyFormat?.replace(
              /{{.*?}}/,
              '{{amount_no_decimals_with_comma_separator}}',
            ),
            value: parseInt(rangeMin.value) * 100,
          })),
      ),
    );

    rangeMax.addEventListener('input', () =>
      maxValueLabel.forEach(
        (label) =>
          (label.innerText = formatPrice({
            format: window?.dAgency?.strings?.moneyFormat?.replace(
              /{{.*?}}/,
              '{{amount_no_decimals_with_comma_separator}}',
            ),
            value: parseInt(rangeMax.value) * 100,
          })),
      ),
    );
  }

  form
    .querySelectorAll('.filters__accordion .filters__search input[type="text"]')
    .forEach((search) => {
      const labelTexts = search
        .closest('.filters__accordion')
        .querySelectorAll('label span:not(.filters__value-swatch)');

      search.addEventListener('input', () =>
        labelTexts.forEach((text) => {
          const label = text.closest('label');
          label.style.display =
            !search.value ||
            text.innerText.toLowerCase().indexOf(search.value.toLowerCase()) >=
              0
              ? ''
              : 'none';
        }),
      );
    });
};

execOnLoad(window.dAgency.initFilters, '.filters__container');
